import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CoursesService } from '../shared/services/courses.service';
import { VideosService } from '../shared/services/videos.service';
import { AssignedCourseItem, Course, CourseResponse } from '../shared/models/course';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  // Page-level state
  readonly loading = signal<boolean>(false);
  readonly errorText = signal<string>('');
  readonly assigned = signal<AssignedCourseItem[]>([]);
  private errorTimer: any = null;

  // Resolved course names for quick display in the table
  readonly courseNameMap = signal<Map<string, string>>(new Map());

  // Expand/collapse state and per-course detail cache
  readonly expanded = signal<Set<string>>(new Set());
  readonly detailsMap = signal<Map<string, { loading: boolean; error?: string; course?: Course }>>(new Map());

  // Cache of presigned video URLs per courseId -> (videoKey -> url)
  readonly videoUrlMap = signal<Map<string, Map<string, string>>>(new Map());
  // Set of existing video keys from the library
  readonly existingVideoKeys = signal<Set<string>>(new Set());
  // Per-course status update busy state
  readonly statusUpdating = signal<Set<string>>(new Set());

  // Course status constants and labels for template use
  readonly STATUS = {
    ASSIGNED: 'ASSIGNED',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED'
  } as const;

  private readonly STATUS_LABEL: Record<string, string> = {
    ASSIGNED: 'Assigned',
    IN_PROGRESS: 'In Progress',
    COMPLETED: 'Completed'
  };

  constructor(
    private readonly courses: CoursesService,
    private readonly videos: VideosService
  ) {}

  ngOnInit(): void {
    this.load();
    this.loadVideoKeys();
  }

  private load(): void {
    this.loading.set(true);
    this.errorText.set('');
    this.courses.getAssignedCourses().subscribe({
      next: (rows) => {
        const list: any[] = Array.isArray(rows) ? rows as any[] : [];
        this.assigned.set(list as any);

        const ids = Array.from(new Set(list.map((r: any) => r.COURSE_ID).filter((v: any) => !!v)));
        if (ids.length === 0) {
          this.courseNameMap.set(new Map());
          this.loading.set(false);
          return;
        }

        const requests = ids.map((id: string) =>
          this.courses
            .getCourse(id)
            .pipe(
              map((res: CourseResponse) => ({ id, name: res?.body?.COURSE_NAME ?? id })),
              catchError(() => of({ id, name: id }))
            )
        );

        forkJoin(requests).subscribe({
          next: (pairs: any[]) => {
            const m = new Map<string, string>();
            for (const p of pairs) {
              m.set(p.id, p.name);
            }
            this.clearError();
            this.courseNameMap.set(m);
            this.loading.set(false);
          },
          error: (err) => {
            this.setError(this.describeError(err));
            this.loading.set(false);
          }
        });
      },
      error: (err) => {
        this.setError(this.describeError(err));
        this.loading.set(false);
      }
    });
  }

  private loadVideoKeys(): void {
    this.videos.listVideos().pipe(catchError(() => of({ body: { videos: [] } } as any))).subscribe((resp: any) => {
      const keys = new Set<string>();
      for (const v of resp?.body?.videos ?? []) {
        if (v?.key) keys.add(v.key);
      }
      this.existingVideoKeys.set(keys);
    });
  }

  // UI helpers
  badgeClass(status: string): string {
    const s = (status || '').toUpperCase();
    if (s === this.STATUS.COMPLETED) return 'badge badge--green';
    if (s === this.STATUS.IN_PROGRESS) return 'badge badge--orange';
    if (s === this.STATUS.ASSIGNED) return 'badge badge--blue';
    return 'badge';
  }

  statusLabel(status: string): string {
    const key = (status || '').toUpperCase();
    return this.STATUS_LABEL[key] ?? status ?? '';
  }

  getCourseName(courseId?: string | null): string {
    if (!courseId) return '—';
    return this.courseNameMap().get(courseId) ?? '—';
  }

  isActive(courseId: string): boolean {
    return this.expanded().has(courseId);
  }

  toggleExpand(courseId: string): void {
    if (!courseId) return;
    const set = new Set(this.expanded());
    if (set.has(courseId)) {
      set.delete(courseId);
      this.expanded.set(set);
      return;
    }
    set.add(courseId);
    this.expanded.set(set);

    const cache = new Map(this.detailsMap());
    if (!cache.has(courseId)) {
      const next = new Map(this.detailsMap());
      next.set(courseId, { loading: true });
      this.detailsMap.set(next);

      this.courses.getCourse(courseId).subscribe({
        next: (res: CourseResponse) => {
          const m = new Map(this.detailsMap());
          m.set(courseId, { loading: false, course: res.body });
          this.detailsMap.set(m);

          // Prefetch presigned URLs for VIDEO items so links are ready
          if (res?.body?.CONTENT?.length) {
            this.prefetchVideoUrls(courseId, res.body);
          }
        },
        error: (err) => {
          const m = new Map(this.detailsMap());
          m.set(courseId, { loading: false, error: this.describeError(err) });
          this.detailsMap.set(m);
        }
      });
    }
  }

  detailFor(courseId: string) {
    return this.detailsMap().get(courseId);
  }

  // Link helpers
  private setVideoUrl(courseId: string, key: string, url: string) {
    const outer = new Map(this.videoUrlMap());
    const inner = new Map(outer.get(courseId) ?? new Map<string, string>());
    inner.set(key, url);
    outer.set(courseId, inner);
    this.videoUrlMap.set(outer);
  }

  private prefetchVideoUrls(courseId: string, course: Course) {
    for (const item of course.CONTENT ?? []) {
      if (item.CONTENT_TYPE === 'VIDEO') {
        // avoid duplicate requests
        if (this.videoUrl(courseId, item.KEY)) continue;
        this.videos.getPresignedUrl(item.KEY).pipe(catchError(() => of(''))).subscribe((url) => {
          if (url) this.setVideoUrl(courseId, item.KEY, url);
        });
      }
    }
  }

  videoUrl(courseId: string, key: string): string | null {
    return this.videoUrlMap().get(courseId)?.get(key) ?? null;
  }

  videoExists(key: string): boolean {
    return this.existingVideoKeys().has(key);
  }

  onVideoClick(courseId: string, key: string, ev: Event) {
    // Ensure we always open in new tab via JS if URL not bound yet
    const existing = this.videoUrl(courseId, key);
    if (existing) {
      return; // anchor href will handle opening in new tab
    }
    ev.preventDefault();
    this.videos.getPresignedUrl(key).subscribe((url) => {
      if (url) {
        this.setVideoUrl(courseId, key, url);
        window.open(url, '_blank', 'noopener');
      }
    });
  }

  private describeError(err: any): string {
    if (!err) return 'Failed to load data.';
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    if ((err as any).error?.message) return (err as any).error.message;
    return 'Failed to load data.';
  }

  private clearError() {
    this.errorText.set('');
    if (this.errorTimer) {
      clearTimeout(this.errorTimer);
      this.errorTimer = null;
    }
  }

  private setError(message: string) {
    this.errorText.set(message);
    if (this.errorTimer) {
      clearTimeout(this.errorTimer);
    }
    this.errorTimer = setTimeout(() => this.clearError(), 10000);
  }

  dismissError() {
    this.clearError();
  }

  // Status update actions
  canMark(courseId: string, target: 'IN_PROGRESS' | 'COMPLETED', current?: string | null): boolean {
    const cur = (current || '').toUpperCase();
    if (!courseId) return false;
    if (this.statusUpdating().has(courseId)) return false;
    return cur !== target;
  }

  showMarkInProgress(status?: string | null): boolean {
    const s = (status || '').toUpperCase();
    return s === this.STATUS.ASSIGNED;
  }

  showMarkCompleted(status?: string | null): boolean {
    const s = (status || '').toUpperCase();
    return s === this.STATUS.ASSIGNED || s === this.STATUS.IN_PROGRESS;
  }

  buttonsVisible(status?: string | null): boolean {
    return this.showMarkInProgress(status) || this.showMarkCompleted(status);
  }

  markStatus(courseId: string, target: 'IN_PROGRESS' | 'COMPLETED') {
    const member = sessionStorage.getItem('current-email') || sessionStorage.getItem('current-username') || '';
    if (!member || !courseId) return;
    const busy = new Set(this.statusUpdating());
    busy.add(courseId);
    this.statusUpdating.set(busy);

    this.courses.updateMemberCourseStatus(member, courseId, target).subscribe({
      next: () => {
        // update assigned list in-place
        const updated = (this.assigned() || []).map((row: any) => {
          if (row?.COURSE_ID === courseId) {
            return { ...row, STATUS: target };
          }
          return row;
        });
        this.assigned.set(updated as any);
        const done = new Set(this.statusUpdating());
        done.delete(courseId);
        this.statusUpdating.set(done);
      },
      error: (err) => {
        this.setError(this.describeError(err));
        const done = new Set(this.statusUpdating());
        done.delete(courseId);
        this.statusUpdating.set(done);
      }
    });
  }
}


